/**
 * 68.42: the planet table's flat projection (the owner's B) — the map's ground and its projection. Azimuthal
 * equidistant about the theatre's centre (ui/topoMap.ts `toMap`, the frame's radius in people), so map distance is walking
 * distance; your base toward the bottom, theirs toward the top. The ground: the planet's colours toned to the table's
 * slate, hillshaded, contoured, smoothed (the coarse mesh is flat per triangle), with the fronts' tint (the scene's
 * control field) over it, and territory's contested band shaded where the scene has one. Sampled every 3 px of the view, once per view (a pan or zoom resamples). Browser only.
 */
import type { PlanetState } from '../types';
import { getSurfaceRadius, nearestSurfaceVertex } from '../core/surface';
import { computePlanetVertexColors } from '../core/planetColors';
import { fromMap, contourInterval, type TopoFrame } from './topoMap';
import type { M, TableProj } from './planetTableDraw';

/** A window over the map: its centre and the half-width of its shorter side, in people. */
export interface MapWindow { x: number; y: number; extent: number }

/** The flat map's projection for a window drawn `w` × `h` px. */
export function mapProjection(v: MapWindow, w: number, h: number): TableProj {
  const s = Math.min(w, h) / (2 * v.extent), cx = w / 2, cy = h / 2;
  return { at: m => ({ x: cx + (m.x - v.x) * s, y: cy - (m.y - v.y) * s, ok: true }), ppp: () => s };
}

const colorCache = new WeakMap<Float32Array, Float32Array>();
const hex = (c: string): [number, number, number] => { const n = parseInt(c.slice(1), 16); return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255]; };

/**
 * The map's ground for window `v` (`w` × `h` px), sampled every `cell` px a few rows at a time (`step` within a budget), then finished
 * into `canvas` — a pan or zoom must not stall the frame (a full window is ~150,000 ground samples). `frame` in people
 * (its radius the planet's in people), `person` a person's height in world units, `control` the fronts (positive yours),
 * `own` / `them` the sides' colours, `contested` territory's band (0–1) when on. Past the planet's far side stays dark.
 */
export class MapGroundJob {
  readonly canvas = document.createElement('canvas');
  private row = 0;
  private readonly nx: number;
  private readonly ny: number;
  private readonly hs: Float32Array;
  private readonly col: Float32Array;
  private readonly ctl: Float32Array;
  private readonly band: Float32Array | null;
  private readonly inside: Uint8Array;
  private readonly colors: Float32Array;
  done = false;

  constructor(private readonly planet: PlanetState, private readonly frame: TopoFrame, private readonly person: number, readonly v: MapWindow, readonly w: number, readonly h: number, private readonly control: (m: M) => number, private readonly own: string, private readonly them: string, readonly cell = 3, private readonly contested?: (m: M) => number) {
    this.nx = Math.ceil(w / cell); this.ny = Math.ceil(h / cell);
    const N = this.nx * this.ny;
    this.hs = new Float32Array(N); this.col = new Float32Array(N * 3); this.ctl = new Float32Array(N); this.band = contested ? new Float32Array(N) : null; this.inside = new Uint8Array(N);
    let colors = colorCache.get(planet.heightmap);
    if (!colors) { colors = computePlanetVertexColors(planet); colorCache.set(planet.heightmap, colors); }
    this.colors = colors;
    this.canvas.width = w; this.canvas.height = h;
  }

  /** Sample rows for up to `ms` (at least one); finish when the last is in. True when the ground is drawn. */
  step(ms = Infinity): boolean {
    if (this.done) return true;
    const { nx, ny, v, w, h } = this, s = Math.min(w, h) / (2 * v.extent), px = this.cell / s, R = this.frame.radius, x0 = v.x - w / 2 / s, y0 = v.y + h / 2 / s, t0 = performance.now();
    for (let first = true; this.row < ny && (first || performance.now() - t0 < ms); first = false) {
      const j = this.row++;
      for (let i = 0; i < nx; i++) {
        const m = { x: x0 + (i + 0.5) * px, y: y0 - (j + 0.5) * px }, k = j * nx + i;
        if (Math.hypot(m.x, m.y) > Math.PI * R) continue;   // past the planet's far side
        this.inside[k] = 1;
        const d = fromMap(this.frame, m.x, m.y);
        this.hs[k] = (getSurfaceRadius(this.planet, d) - this.planet.radius) / this.person;
        const vi = nearestSurfaceVertex(this.planet, d) * 3;
        this.col[k * 3] = this.colors[vi]; this.col[k * 3 + 1] = this.colors[vi + 1]; this.col[k * 3 + 2] = this.colors[vi + 2];
        this.ctl[k] = this.control(m);
        if (this.band) this.band[k] = this.contested!(m);
      }
    }
    if (this.row < ny) return false;
    // then the finish, a phase a call when sliced (the heights smoothed, the colours smoothed, the picture composed)
    for (let first = true; this.phase < 3 && (first || performance.now() - t0 < ms); first = false) this.finish(this.phase++, s, px);
    this.done = this.phase >= 3;
    return this.done;
  }
  private phase = 0;

  private finish(phase: number, s: number, px: number): void {
    const { nx, ny, hs, col, ctl, inside, w, h, v } = this, N = nx * ny, R = this.frame.radius, person = this.person;
    // smoothed: the coarse mesh is flat per triangle, and its colour per vertex
    const blur = (a: Float32Array, stride: number, r: number) => {
      for (let pass = 0; pass < 2; pass++) for (const [di, dj] of [[1, 0], [0, 1]]) {
        const b = a.slice();
        for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) for (let c3 = 0; c3 < stride; c3++) {
          let sum = 0, cnt = 0;
          for (let t = -r; t <= r; t++) { const ii = i + di * t, jj = j + dj * t; if (ii < 0 || jj < 0 || ii >= nx || jj >= ny || !inside[jj * nx + ii]) continue; sum += b[(jj * nx + ii) * stride + c3]; cnt++; }
          if (cnt) a[(j * nx + i) * stride + c3] = sum / cnt;
        }
      }
    };
    const br = Math.max(1, Math.round(9 / this.cell));   // the same smoothing on the ground at any sampling
    if (phase === 0) { blur(hs, 1, br); return; }
    if (phase === 1) { blur(col, 3, br); return; }
    let lo = Infinity, hi = -Infinity; for (let k = 0; k < N; k++) if (inside[k]) { lo = Math.min(lo, hs[k]); hi = Math.max(hi, hs[k]); }
    const interval = contourInterval((hi - lo) * person, person) / person;
    const img = new ImageData(nx, ny), at = (i: number, j: number) => hs[Math.min(ny - 1, Math.max(0, j)) * nx + Math.min(nx - 1, Math.max(0, i))];
    const ownC = hex(this.own), themC = hex(this.them), slate = [0.30, 0.32, 0.30], bandC = [0.84, 0.78, 0.64];
    for (let j = 0; j < ny; j++) for (let i = 0; i < nx; i++) {
      const k = j * nx + i; if (!inside[k]) continue;
      const dx = (at(i + 1, j) - at(i - 1, j)) / (2 * px), dy = (at(i, j - 1) - at(i, j + 1)) / (2 * px), nl = Math.hypot(dx * 3, dy * 3, 1);
      const shade = Math.max(0.55, Math.min(1.2, 0.4 + 0.8 * ((0.6 * dx * 3 - 0.6 * dy * 3 + 0.53) / nl)));
      const band = Math.floor(hs[k] / interval), edge = band !== Math.floor(at(i + 1, j) / interval) || band !== Math.floor(at(i, j + 1) / interval);
      const contour = edge ? (((band % 5) + 5) % 5 === 0 ? 0.72 : 0.86) : 1;
      const t = Math.max(-1, Math.min(1, ctl[k] * 1.4)), b = this.band ? Math.min(1, this.band[k] * 1.6) : 0, tint = Math.max(Math.min(0.32, Math.abs(t) * 0.32), b * 0.3), tc = t > 0 ? ownC : themC;
      for (let c3 = 0; c3 < 3; c3++) {
        const base = (0.45 * Math.pow(col[k * 3 + c3], 1 / 2.2) + 0.55 * slate[c3]) * shade * contour, over = tc[c3] + (bandC[c3] - tc[c3]) * b;
        img.data[k * 4 + c3] = Math.round(255 * Math.min(1, base * (1 - tint) + tint * over * 0.9));
      }
      img.data[k * 4 + 3] = 255;
    }
    const tmp = document.createElement('canvas'); tmp.width = nx; tmp.height = ny; tmp.getContext('2d')!.putImageData(img, 0, 0);
    const g = this.canvas.getContext('2d')!;
    g.fillStyle = '#0b0d14'; g.fillRect(0, 0, w, h);
    g.imageSmoothingEnabled = true;
    g.drawImage(tmp, 0, 0, nx * this.cell, ny * this.cell);
    if (v.extent >= Math.PI * R * 0.5) {   // the planet's zoom: the centre's horizon dashed (the far side squeezed past it)
      const p = mapProjection(v, w, h), c = p.at({ x: 0, y: 0 });
      g.strokeStyle = 'rgba(200,210,230,0.35)'; g.lineWidth = 1.5; g.setLineDash([4, 6]); g.beginPath(); g.arc(c.x, c.y, (Math.PI * R * s) / 2, 0, Math.PI * 2); g.stroke(); g.setLineDash([]);
    }
  }
}

/** The map's ground drawn at once into `g` (the sheet's way; play steps a MapGroundJob). */
export function drawMapGround(g: CanvasRenderingContext2D, planet: PlanetState, frame: TopoFrame, person: number, v: MapWindow, w: number, h: number, control: (m: M) => number, own: string, them: string, contested?: (m: M) => number): void {
  const job = new MapGroundJob(planet, frame, person, v, w, h, control, own, them, 3, contested);
  job.step();
  g.drawImage(job.canvas, 0, 0, w, h);
}
